import PermissionButton from 'components/buttons/PermissionButton';
import FormRow from 'components/forms/FormRow';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import AddressInput from 'components/forms/inputs/rhf-inputs/AddressInput';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import PhoneNumberInput from 'components/forms/inputs/rhf-inputs/PhoneNumberInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import ContactDynamicMultiSelectPicker from 'components/party/ContactDynamicMultiSelectPicker';
import ContactDynamicSelectPicker from 'components/party/ContactDynamicSelectPicker';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { FormattedMessage } from 'react-intl';
import {
    useLazyGetMinContactsListWithFiltersQuery,
    useLazyGetMinContactsWithRelationsListWithFiltersQuery,
} from 'store/api/contacts';
import { useLazyGetMinGroupsListWithFiltersQuery } from 'store/api/groups';
import { ContactMinDtoWithRelationsDto, PartyType } from 'types/contacts';
import { CustomField } from 'types/customFields';
import { t } from 'utils/getTranslations';
import { GroupInfo } from '../GroupInfo';
import { ContactFormState } from './useContactForm';

/** Contact details: phones, e-mail, addresses, a company's employees and primary contact, contact groups. */
export const ContactDetailsFields = ({ form }: { form: ContactFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    const {
        classifiers,
        contact,
        isCorrespondenceAddressEnabled,
        isEmailMandatory,
        isPrimaryContactRequired,
        openAddNewContactModal,
        partyType,
        selectedEmployeeIds,
        validEmail,
    } = form;
    // On the record page a group's chip carries the group's info icon, as the read view's chip does: as wide in both.
    const inSection = useInRecordSection();

    return (
        <>
            <FormRow>
                <PhoneNumberInput name="mainPhone" control={control} label="phone" placeholder="phone" />
                <PhoneNumberInput name="mainMobile" control={control} label="mobile-phone" placeholder="mobile-phone" />
            </FormRow>
            <TextInput
                name="mainEmail"
                control={control}
                label="email"
                placeholder="email"
                maxLength={255}
                validate={validEmail}
                required={isEmailMandatory}
            />
            {partyType == PartyType.PERSON && isCorrespondenceAddressEnabled ? (
                <>
                    <AddressInput
                        label="contacts.correspondence-address"
                        name="correspondenceAddress"
                        control={control}
                        classifiers={classifiers}
                        customFields={contact?.correspondenceAddress?.customFields as CustomField[]}
                    />
                </>
            ) : null}
            {partyType !== PartyType.PERSON ? (
                <>
                    {isCorrespondenceAddressEnabled && (
                        <AddressInput
                            label="contacts.correspondence-address"
                            name="correspondenceAddress"
                            control={control}
                            classifiers={classifiers}
                            customFields={contact?.correspondenceAddress?.customFields as CustomField[]}
                        />
                    )}
                    <AddressInput
                        label="visiting-address"
                        name="visitingAddress"
                        control={control}
                        classifiers={classifiers}
                        customFields={contact?.visitingAddress?.customFields as CustomField[]}
                    />
                    <ContactDynamicMultiSelectPicker
                        setValue={setValue}
                        control={control}
                        label="employees.employees"
                        name="employees"
                        paginated={{
                            fetch: useLazyGetMinContactsListWithFiltersQuery,
                            filters: `partyType==${PartyType.PERSON}`,
                            searchField: ['name', 'partyNumber'],
                        }}
                        isPickerModalOptionEnabled={(option) => option.partyType === PartyType.PERSON}
                        placeholder="employees.employees"
                        noOptionsContent={
                            <PermissionButton
                                permissions={[
                                    'CREATE.PARTIES',
                                    'CREATE.PARTIES.CLIENTS',
                                    'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                                ]}
                                variant="contained"
                                color="secondary"
                                size="extraSmall"
                                onClick={() => openAddNewContactModal('employees')}
                            >
                                <FormattedMessage id="contacts.add-new-contact" />
                            </PermissionButton>
                        }
                        PickerModalComponent={ContactPickerModal}
                    />
                    <ContactDynamicSelectPicker
                        setValue={setValue}
                        control={control}
                        label="contacts.primary-contact"
                        name="primaryContact"
                        paginated={{
                            fetch: selectedEmployeeIds.length
                                ? useLazyGetMinContactsWithRelationsListWithFiltersQuery
                                : useLazyGetMinContactsListWithFiltersQuery,
                            filters: `partyType==${PartyType.PERSON}`,
                            searchField: ['name', 'partyNumber'],
                            sort: 'name,asc',
                            additionalParams:
                                selectedEmployeeIds.length > 0 ? { prioritizeParties: selectedEmployeeIds } : undefined,
                        }}
                        {...(selectedEmployeeIds.length && {
                            groupByStaticFields: (option: ContactMinDtoWithRelationsDto) => {
                                const employedByContact = selectedEmployeeIds.includes(option.id);

                                if (employedByContact) {
                                    return t('field.employed-by-contact');
                                }

                                return t('field.other-people');
                            },
                        })}
                        isPickerModalOptionEnabled={(option) => option.partyType === PartyType.PERSON}
                        placeholder="contacts.primary-contact"
                        PickerModalComponent={ContactPickerModal}
                        noOptionsContent={
                            <PermissionButton
                                permissions={[
                                    'CREATE.PARTIES',
                                    'CREATE.PARTIES.CLIENTS',
                                    'CREATE.PARTIES.LEADS_AND_NON_CLIENTS',
                                ]}
                                variant="contained"
                                color="secondary"
                                size="extraSmall"
                                onClick={() => openAddNewContactModal('primaryContact')}
                            >
                                <FormattedMessage id="contacts.add-new-contact" />
                            </PermissionButton>
                        }
                        required={isPrimaryContactRequired}
                    />
                </>
            ) : null}
            <DynamicMultiSelectInput
                setValue={setValue}
                control={control}
                label="contacts.contact-groups"
                name="partyGroups"
                paginated={{
                    fetch: useLazyGetMinGroupsListWithFiltersQuery,
                    searchField: 'groupName',
                }}
                getOptionLabel="groupName"
                getOptionIdentifier="id"
                placeholder="contacts.contact-groups"
                getSelectedValueEndAdornment={
                    inSection ? (group) => <GroupInfo group={group} classifiers={classifiers} /> : undefined
                }
            />
        </>
    );
};
